'use client';

import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslations } from 'next-intl';
import { Box, HStack, VStack, token } from '@terra/ui-styled-system';
import {
  AlertBanner,
  Button,
  Field,
  Input,
  LabelDefault,
  Popover,
  Text,
} from '@terra/ui-design-system';
import { LinkSquare02Icon, Search01Icon, Cancel01Icon } from '@terra/ui-icons';
import type { DiscoveryContentTypeConfig } from '@terra/shared-asset-processing';
import {
  isValidDiscoveryContentType,
  NO_MIME_TYPE,
  normalizeMimeKey,
  resolveDiscoveryContentTypes,
} from '@terra/shared-asset-processing';
import { navigateToApplicationSettingsSubTab } from '../../utils/navigateToApplicationSettingsSubTab';

type TruncatedMimeLabelProps = {
  label: string;
  color: string;
};
type TruncationTooltipPosition = {
  top: number;
  left: number;
};

function TruncatedMimeLabel({ label, color }: TruncatedMimeLabelProps) {
  const [tooltipPosition, setTooltipPosition] =
    useState<TruncationTooltipPosition | null>(null);

  return (
    <>
      <Box
        minW="0"
        flex="1"
        overflow="hidden"
        textOverflow="ellipsis"
        whiteSpace="nowrap"
        fontSize="xs"
        fontFamily="mono"
        fontWeight="normal"
        lineHeight="short"
        color={color}
        onMouseEnter={(event) => {
          const el = event.currentTarget;

          if (el.scrollWidth <= el.clientWidth) {
            setTooltipPosition(null);

            return;
          }

          const rect = el.getBoundingClientRect();

          setTooltipPosition({
            top: rect.bottom + 4,
            left: rect.left,
          });
        }}
        onMouseLeave={() => setTooltipPosition(null)}
      >
        {label}
      </Box>
      {tooltipPosition
        ? createPortal(
            <div
              style={{
                position: 'fixed',
                top: tooltipPosition.top,
                left: tooltipPosition.left,
                zIndex: 2147483646,
                maxWidth: 320,
                padding: '6px 8px',
                borderRadius: 6,
                border: `1px solid ${token('colors.border.tooltip')}`,
                background: token('colors.bg.tooltip'),
                color: token('colors.fg.tooltip.default'),
                pointerEvents: 'none',
                fontSize: 12,
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                fontWeight: 400,
                lineHeight: 1.3,
                wordBreak: 'break-all',
                whiteSpace: 'normal',
              }}
            >
              {label}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

type SessionContentTypesSectionProps = {
  value: string[];
  onChange: (mimeTypes: string[]) => void;
  applicationConfigs: DiscoveryContentTypeConfig[] | null | undefined;
  onNavigateToSettings?: () => void;
};

export function SessionContentTypesSection({
  value,
  onChange,
  applicationConfigs,
  onNavigateToSettings,
}: SessionContentTypesSectionProps) {
  const t = useTranslations('proxy.tracking.sessionContentTypes');
  const tCommon = useTranslations('common');
  const [isAddMenuOpen, setIsAddMenuOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [addError, setAddError] = useState<string | null>(null);
  const noMimeTypeLabel = t('noMimeType');
  const displayMimeLabel = (mimeType: string): string =>
    mimeType === NO_MIME_TYPE ? noMimeTypeLabel : mimeType;
  const resolvedConfigs = useMemo(
    () => resolveDiscoveryContentTypes(applicationConfigs),
    [applicationConfigs],
  );
  const configByMime = useMemo(
    () =>
      new Map(
        resolvedConfigs.map((config) => [
          normalizeMimeKey(config.mimeType),
          config,
        ]),
      ),
    [resolvedConfigs],
  );
  const activeKeys = useMemo(
    () => new Set(value.map((mimeType) => normalizeMimeKey(mimeType))),
    [value],
  );
  const availableFromSettings = useMemo(
    () =>
      resolvedConfigs.filter(
        (config) => !activeKeys.has(normalizeMimeKey(config.mimeType)),
      ),
    [resolvedConfigs, activeKeys],
  );
  const filteredAvailable = useMemo(() => {
    const trimmed = query.trim().toLowerCase();

    if (!trimmed) {
      return availableFromSettings;
    }

    return availableFromSettings.filter((config) => {
      const mime = config.mimeType.toLowerCase();
      const label = (
        config.mimeType === NO_MIME_TYPE ? noMimeTypeLabel : config.mimeType
      ).toLowerCase();

      return mime.includes(trimmed) || label.includes(trimmed);
    });
  }, [availableFromSettings, query, noMimeTypeLabel]);
  const canCreateFromQuery = useMemo(() => {
    const trimmed = query.trim();

    if (!trimmed || !isValidDiscoveryContentType(trimmed)) {
      return false;
    }

    return !activeKeys.has(normalizeMimeKey(trimmed));
  }, [query, activeKeys]);
  const isSessionCreatedType = (mimeType: string): boolean =>
    !configByMime.has(normalizeMimeKey(mimeType));
  const removeType = (mimeType: string) => {
    const key = normalizeMimeKey(mimeType);

    onChange(value.filter((item) => normalizeMimeKey(item) !== key));
  };

  const addType = (mimeType: string) => {
    const trimmed = mimeType.trim();

    if (!isValidDiscoveryContentType(trimmed)) {
      setAddError(t('invalidMimeType'));

      return;
    }

    const key = normalizeMimeKey(trimmed);

    if (activeKeys.has(key)) {
      setAddError(t('alreadyActive'));

      return;
    }

    const fromSettings = configByMime.get(key);

    onChange([...value, fromSettings?.mimeType ?? trimmed]);
    setIsAddMenuOpen(false);
    setQuery('');
    setAddError(null);
  };

  const handleOpenSettings = () => {
    navigateToApplicationSettingsSubTab('automation');
    onNavigateToSettings?.();
  };

  const handleAddMenuOpenChange = ({ open }: { open: boolean }) => {
    setIsAddMenuOpen(open);
    if (!open) {
      setQuery('');
      setAddError(null);
    }
  };

  return (
    <VStack gap="2" w="full" alignItems="flex-start">
      <HStack
        gap="2"
        w="full"
        alignItems="flex-start"
        justifyContent="space-between"
      >
        <VStack gap="1" alignItems="flex-start" flex="1" minW="0">
          <LabelDefault fontWeight="semibold" color="fg.muted">
            {t('title')}
          </LabelDefault>
          <Text fontSize="xs" color="fg.subtle">
            {t('helperText')}
          </Text>
        </VStack>
        <Button
          variant="link"
          size="xs"
          flexShrink={0}
          mt="1"
          onClick={handleOpenSettings}
          color="terra.9"
        >
          <HStack gap="1" alignItems="center">
            {t('editDefaults')}
            <LinkSquare02Icon size={12} />
          </HStack>
        </Button>
      </HStack>

      <Box
        w="full"
        borderWidth="1px"
        borderColor="border.subtle"
        borderRadius="md"
        bg="bg.default"
        p="3"
      >
        <VStack gap="2" w="full" alignItems="flex-start">
          <HStack gap="1.5" flexWrap="wrap" w="full" alignItems="center">
            {value.map((mimeType) => {
              const sessionCreated = isSessionCreatedType(mimeType);

              return (
                <HStack
                  key={mimeType}
                  gap="1"
                  alignItems="center"
                  px="2"
                  py="0.5"
                  minH="6"
                  borderRadius="md"
                  borderWidth="1px"
                  borderStyle="solid"
                  borderColor={sessionCreated ? 'terra.8' : 'border.subtle'}
                  bg={sessionCreated ? 'orange.2' : 'bg.light'}
                  color={sessionCreated ? 'terra.9' : 'fg.muted'}
                >
                  <Text
                    fontSize="xs"
                    fontFamily="mono"
                    fontWeight="normal"
                    lineHeight="short"
                  >
                    {displayMimeLabel(mimeType)}
                  </Text>
                  <Box
                    as="span"
                    cursor="pointer"
                    display="inline-flex"
                    alignItems="center"
                    color={sessionCreated ? 'terra.9' : 'fg.muted'}
                    aria-label={tCommon('remove')}
                    onClick={() => removeType(mimeType)}
                    _hover={{ opacity: 0.7 }}
                  >
                    <Cancel01Icon size={10} />
                  </Box>
                </HStack>
              );
            })}

            <Popover.Root
              open={isAddMenuOpen}
              onOpenChange={handleAddMenuOpenChange}
              positioning={{ placement: 'bottom-start', strategy: 'fixed' }}
              closeOnInteractOutside
            >
              <Popover.Trigger asChild>
                <Box
                  as="button"
                  display="inline-flex"
                  alignItems="center"
                  px="2"
                  py="0.5"
                  minH="6"
                  borderRadius="md"
                  borderWidth="1px"
                  borderStyle="dashed"
                  borderColor="terra.9"
                  color="terra.9"
                  bg="transparent"
                  cursor="pointer"
                  fontSize="xs"
                  lineHeight="short"
                  _hover={{ bg: 'bg.muted' }}
                >
                  {t('addType')}
                </Box>
              </Popover.Trigger>

              <Popover.Positioner>
                <Popover.Content
                  minW="280px"
                  maxW="320px"
                  px="1.5"
                  py="1.5"
                  bg="bg.default"
                  borderRadius="md"
                  borderWidth="1px"
                  borderColor="border.subtle"
                  boxShadow="lg"
                >
                  <VStack gap="1.5" w="full" alignItems="stretch">
                    <Field.Root invalid={!!addError}>
                      <Box position="relative" w="full">
                        <Box
                          position="absolute"
                          left="2"
                          top="50%"
                          transform="translateY(-50%)"
                          display="inline-flex"
                          alignItems="center"
                          pointerEvents="none"
                          color="fg.muted"
                        >
                          <Search01Icon size={14} />
                        </Box>
                        <Input
                          size="sm"
                          value={query}
                          placeholder={t('filterPlaceholder')}
                          paddingLeft="8"
                          onChange={(event) => {
                            setQuery(event.target.value);
                            setAddError(null);
                          }}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' && canCreateFromQuery) {
                              event.preventDefault();
                              addType(query);
                            }
                          }}
                        />
                      </Box>
                      {addError ? (
                        <Field.ErrorText>{addError}</Field.ErrorText>
                      ) : null}
                    </Field.Root>

                    <VStack
                      gap="0"
                      w="full"
                      alignItems="stretch"
                      maxH="48"
                      overflowY="auto"
                    >
                      {filteredAvailable.length === 0 && !canCreateFromQuery ? (
                        <Text fontSize="xs" color="fg.subtle" px="1.5" py="1.5">
                          {t('noMatchingTypes')}
                        </Text>
                      ) : null}
                      {filteredAvailable.map((config) => (
                        <HStack
                          key={config.mimeType}
                          w="full"
                          gap="2"
                          px="1.5"
                          py="1.5"
                          justifyContent="space-between"
                          alignItems="center"
                          borderRadius="sm"
                          cursor="pointer"
                          bg="transparent"
                          role="button"
                          tabIndex={0}
                          onClick={() => addType(config.mimeType)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              addType(config.mimeType);
                            }
                          }}
                          _hover={{ bg: 'bg.muted' }}
                        >
                          <TruncatedMimeLabel
                            label={displayMimeLabel(config.mimeType)}
                            color="fg.muted"
                          />
                          {!config.torchTrackingEnabled ? (
                            <Text
                              fontSize="xs"
                              fontWeight="normal"
                              color="fg.subtle"
                              flexShrink={0}
                            >
                              {t('disabledInSettings')}
                            </Text>
                          ) : null}
                        </HStack>
                      ))}
                      {canCreateFromQuery ? (
                        <Box
                          as="button"
                          w="full"
                          px="1.5"
                          py="1.5"
                          borderRadius="sm"
                          cursor="pointer"
                          bg="transparent"
                          borderWidth="0"
                          textAlign="left"
                          onClick={() => addType(query)}
                          _hover={{ bg: 'bg.muted' }}
                        >
                          <Text
                            fontSize="xs"
                            fontFamily="mono"
                            fontWeight="normal"
                            lineHeight="short"
                            color="terra.9"
                          >
                            {t('addForSession', { mimeType: query.trim() })}
                          </Text>
                        </Box>
                      ) : null}
                    </VStack>
                  </VStack>
                </Popover.Content>
              </Popover.Positioner>
            </Popover.Root>
          </HStack>

          {value.length === 0 ? (
            <AlertBanner
              kind="attention"
              type="outline"
              description={t('emptyTypesWarning')}
            />
          ) : null}
        </VStack>
      </Box>

      <Text fontSize="xs" color="fg.subtle">
        {t('activeCount', { count: value.length })}
      </Text>
    </VStack>
  );
}
